import { useState } from 'react';
import { useI18n } from '@/i18n';
import {
  Badge,
  Button,
  Card,
  Input,
  Spinner,
} from '@/components/ui';
import {
  useCACKycChecks,
  useCACPartnerProfilesList,
  useCACRejectTierTransition,
  useCACResetKycCheck,
  useCACReviewKycCheck,
  useCACTierTransitions,
  useCACTransitionTier,
} from '@/features/cac';
import type { KycCheck, PartnerProfile, PartnerTier } from '@/features/cac';

const TIER_BADGE_CLASS: Record<PartnerTier, string> = {
  verified: 'bg-blue-50 text-blue-700 border-blue-200',
  premium: 'bg-amber-50 text-amber-700 border-amber-200',
  enterprise: 'bg-purple-50 text-purple-700 border-purple-200',
};

const KYC_STATUS_BADGE_CLASS: Record<string, string> = {
  pending: 'bg-gray-50 text-gray-600 border-gray-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  expired: 'bg-amber-50 text-amber-700 border-amber-200',
};

function publicErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Operation failed.';
  const maybeAxios = error as {
    response?: { data?: { detail?: { message?: string } } };
  };
  return maybeAxios.response?.data?.detail?.message ?? 'Operation failed. Please retry.';
}

export function PartnerAdminPage() {
  const { t: _t } = useI18n();
  void _t;

  const [filterTier, setFilterTier] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});

  const profilesQuery = useCACPartnerProfilesList(filterTier || undefined, undefined);
  const profiles = profilesQuery.data ?? [];

  return (
    <div className="space-y-6" data-testid="partner-admin-page">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Partner Management</h2>
        <select
          className="border border-gray-200 dark:border-gray-700 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-900"
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
        >
          <option value="">All tiers</option>
          <option value="verified">verified</option>
          <option value="premium">premium</option>
          <option value="enterprise">enterprise</option>
        </select>
      </div>

      {profilesQuery.isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : profiles.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">No partners found.</Card>
      ) : (
        <div className="space-y-3">
          {profiles.map((p: PartnerProfile) => (
            <PartnerAdminRow
              key={p.id}
              profile={p}
              expanded={selectedTenant === p.tenant_id}
              onToggle={() => setSelectedTenant(selectedTenant === p.tenant_id ? null : p.tenant_id)}
              reviewNotes={reviewNotes}
              setReviewNotes={setReviewNotes}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PartnerAdminRow({
  profile,
  expanded,
  onToggle,
  reviewNotes,
  setReviewNotes,
}: {
  profile: PartnerProfile;
  expanded: boolean;
  onToggle: () => void;
  reviewNotes: Record<string, string>;
  setReviewNotes: (v: Record<string, string>) => void;
}) {
  const kycQuery = useCACKycChecks(profile.tenant_id);
  const transitionsQuery = useCACTierTransitions(profile.tenant_id);
  const reviewMutation = useCACReviewKycCheck(profile.tenant_id);
  const resetMutation = useCACResetKycCheck(profile.tenant_id);
  const transitionMutation = useCACTransitionTier(profile.tenant_id);
  const rejectMutation = useCACRejectTierTransition(profile.tenant_id);

  const checks = kycQuery.data ?? [];
  const transitions = transitionsQuery.data ?? [];
  const pendingUpgrade = transitions.find((tr) => tr.status === 'pending' && tr.transition_type === 'upgrade');

  const handleReview = async (check: KycCheck, decision: 'approve' | 'reject') => {
    await reviewMutation.mutateAsync({
      checkSlug: check.check_slug,
      payload: {
        decision,
        reviewed_by_id: 'admin',
        review_notes: reviewNotes[check.id] || undefined,
      },
    });
  };

  const handleTransition = async (type: string, toTier?: string) => {
    await transitionMutation.mutateAsync({
      transition_type: type,
      to_tier: toTier,
    });
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{profile.display_name}</p>
              <Badge variant="outline" className={TIER_BADGE_CLASS[profile.tier] ?? ''}>{profile.tier}</Badge>
              <Badge variant="outline">{profile.verification_status}</Badge>
            </div>
            <p className="text-xs text-muted-foreground font-mono mt-0.5">{profile.tenant_id}</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{expanded ? '−' : '+'}</span>
      </div>

      {expanded ? (
        <div className="mt-4 space-y-4">
          {(reviewMutation.isError || resetMutation.isError || transitionMutation.isError || rejectMutation.isError) ? (
            <p className="text-sm text-destructive">
              {publicErrorMessage(
                reviewMutation.error ?? resetMutation.error ?? transitionMutation.error ?? rejectMutation.error
              )}
            </p>
          ) : null}

          {pendingUpgrade ? (
            <div className="border border-amber-200 rounded-md p-3">
              <p className="text-sm font-medium">Pending tier upgrade: {pendingUpgrade.from_tier} → {pendingUpgrade.to_tier}</p>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleTransition('upgrade', pendingUpgrade.to_tier)}
                  disabled={transitionMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => rejectMutation.mutateAsync()}
                  disabled={rejectMutation.isPending}
                >
                  Reject
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex gap-2">
            {profile.verification_status !== 'suspended' ? (
              <Button variant="outline" size="sm" onClick={() => handleTransition('suspend')}>
                Suspend
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => handleTransition('reactivate')}>
                Reactivate
              </Button>
            )}
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">KYC Checks</h4>
            <div className="space-y-2">
              {checks.map((check: KycCheck) => (
                <div key={check.id} className="flex items-center justify-between border rounded-md p-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{check.check_slug}</p>
                    <p className="text-xs text-muted-foreground">
                      evidence: {check.evidence_type ?? '—'}
                    </p>
                    {check.status === 'in_progress' ? (
                      <div className="flex gap-2 items-center mt-1">
                        <Input
                          className="max-w-xs"
                          placeholder="Review notes"
                          value={reviewNotes[check.id] ?? ''}
                          onChange={(e) => setReviewNotes({ ...reviewNotes, [check.id]: e.target.value })}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={KYC_STATUS_BADGE_CLASS[check.status] ?? ''}>
                      {check.status}
                    </Badge>
                    {check.status === 'in_progress' ? (
                      <>
                        <Button variant="default" size="sm" onClick={() => handleReview(check, 'approve')} disabled={reviewMutation.isPending}>
                          Approve
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleReview(check, 'reject')} disabled={reviewMutation.isPending}>
                          Reject
                        </Button>
                      </>
                    ) : null}
                    {(check.status === 'approved' || check.status === 'rejected') ? (
                      <Button variant="outline" size="sm" onClick={() => resetMutation.mutateAsync(check.check_slug)} disabled={resetMutation.isPending}>
                        Reset
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
